/**
 * Milestone Check Functions
 * Story 9.2: Milestone Celebration Notifications
 *
 * Functions for checking and awarding milestones based on bot activity.
 * Called asynchronously after message/feedback events.
 */

import type { SupabaseClient } from '@supabase/supabase-js';
import type { MilestoneType } from '../types';
import { getMilestoneTypesForMetric, MILESTONE_THRESHOLDS } from './thresholds';
import type { MilestoneAwardResult } from './types';

/**
 * Check if a bot should receive any question-based milestones
 * Call this after a user message is saved
 *
 * @param supabase - Supabase client (service role)
 * @param botId - Bot ID to check milestones for
 * @returns Array of newly awarded milestones
 */
export async function checkQuestionMilestones(
  supabase: SupabaseClient,
  botId: string
): Promise<MilestoneAwardResult[]> {
  try {
    // Get the total count of user messages for this bot
    const { count, error: countError } = await supabase
      .from('messages')
      .select('*', { count: 'exact', head: true })
      .eq('bot_id', botId)
      .eq('role', 'user');

    if (countError) {
      console.error('[Milestones] Failed to count messages:', countError);
      return [];
    }

    const questionCount = count || 0;
    const questionMilestones = getMilestoneTypesForMetric('questions');
    const results: MilestoneAwardResult[] = [];

    // Check each question-based milestone
    for (const milestoneType of questionMilestones) {
      const threshold = MILESTONE_THRESHOLDS[milestoneType].value;

      // Only check if current count could have crossed this threshold
      if (questionCount >= threshold) {
        const result = await awardMilestoneIfNew(
          supabase,
          botId,
          milestoneType,
          threshold
        );
        if (result.awarded) {
          results.push(result);
        }
      }
    }

    return results;
  } catch (error) {
    console.error('[Milestones] Error checking question milestones:', error);
    return [];
  }
}

/**
 * Check if a bot should receive feedback-based milestones
 * Call this after positive feedback is saved
 *
 * @param supabase - Supabase client (service role)
 * @param botId - Bot ID to check milestones for
 * @param rating - The feedback rating ('positive' or 'negative')
 * @returns Array of newly awarded milestones
 */
export async function checkFeedbackMilestones(
  supabase: SupabaseClient,
  botId: string,
  rating: 'positive' | 'negative'
): Promise<MilestoneAwardResult[]> {
  // Only check for positive feedback milestones
  if (rating !== 'positive') {
    return [];
  }

  try {
    // Get the count of positive feedback for this bot
    const { count, error: countError } = await supabase
      .from('feedback')
      .select('*', { count: 'exact', head: true })
      .eq('bot_id', botId)
      .eq('rating', 'positive');

    if (countError) {
      console.error('[Milestones] Failed to count feedback:', countError);
      return [];
    }

    const positiveFeedbackCount = count || 0;
    const feedbackMilestones = getMilestoneTypesForMetric('feedback');
    const results: MilestoneAwardResult[] = [];

    // Check each feedback-based milestone
    for (const milestoneType of feedbackMilestones) {
      const threshold = MILESTONE_THRESHOLDS[milestoneType].value;

      if (positiveFeedbackCount >= threshold) {
        const result = await awardMilestoneIfNew(
          supabase,
          botId,
          milestoneType,
          threshold
        );
        if (result.awarded) {
          results.push(result);
        }
      }
    }

    return results;
  } catch (error) {
    console.error('[Milestones] Error checking feedback milestones:', error);
    return [];
  }
}

/**
 * Award a milestone if it hasn't already been awarded.
 * Uses INSERT ... ON CONFLICT DO NOTHING for idempotency.
 *
 * This is an internal helper function that handles the actual database
 * insertion of a milestone record. It ensures that each milestone type
 * can only be awarded once per bot through the UNIQUE constraint.
 *
 * @param supabase - Supabase client (must be service role for insert)
 * @param botId - The bot ID to award the milestone to
 * @param milestoneType - The type of milestone being awarded
 * @param thresholdValue - The threshold value that was crossed
 * @returns MilestoneAwardResult indicating if the milestone was newly awarded
 *
 * @example
 * const result = await awardMilestoneIfNew(supabase, botId, 'first_question', 1);
 * if (result.awarded) {
 *   // Trigger notification
 * }
 */
async function awardMilestoneIfNew(
  supabase: SupabaseClient,
  botId: string,
  milestoneType: MilestoneType,
  thresholdValue: number
): Promise<MilestoneAwardResult> {
  try {
    // Try to insert the milestone (ON CONFLICT DO NOTHING makes this idempotent)
    const { data, error } = await supabase
      .from('milestones')
      .insert({
        bot_id: botId,
        milestone_type: milestoneType,
        threshold_value: thresholdValue,
      })
      .select('id')
      .single();

    if (error) {
      // Conflict error (23505) means milestone already exists - not an error
      if (error.code === '23505') {
        return {
          awarded: false,
          milestoneId: null,
          milestoneType,
          thresholdValue,
        };
      }
      console.error('[Milestones] Failed to award milestone:', error);
      return {
        awarded: false,
        milestoneId: null,
        milestoneType: null,
        thresholdValue: null,
      };
    }

    console.log(`[Milestones] Awarded ${milestoneType} to bot ${botId}`);

    return {
      awarded: true,
      milestoneId: data.id,
      milestoneType,
      thresholdValue,
    };
  } catch (error) {
    console.error('[Milestones] Error awarding milestone:', error);
    return {
      awarded: false,
      milestoneId: null,
      milestoneType: null,
      thresholdValue: null,
    };
  }
}

/**
 * Trigger milestone email notification for newly awarded milestones
 * This should be called asynchronously (non-blocking) after awarding
 *
 * @param milestoneId - The newly awarded milestone ID
 * @param creatorAppUrl - Base URL for the creator app
 */
export async function triggerMilestoneNotification(
  milestoneId: string,
  creatorAppUrl: string
): Promise<void> {
  console.log('[Milestones] Triggering notification for milestone:', milestoneId);
  console.log('[Milestones] Creator app URL:', creatorAppUrl);

  try {
    // Call the internal API endpoint to send the notification
    // This is fire-and-forget - we don't wait for the response
    const response = await fetch(
      `${creatorAppUrl}/api/internal/milestones/notify`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // Add internal API key if configured
          ...(process.env.INTERNAL_API_KEY && {
            'X-Internal-API-Key': process.env.INTERNAL_API_KEY,
          }),
        },
        body: JSON.stringify({ milestoneId }),
      }
    );

    if (!response.ok) {
      const text = await response.text();
      console.error(
        '[Milestones] Failed to trigger notification:',
        response.status,
        text
      );
    } else {
      const result = await response.json();
      console.log('[Milestones] Notification triggered successfully:', result);
    }
  } catch (error) {
    // Log but don't throw - this is a best-effort notification
    console.error('[Milestones] Error triggering notification:', error);
  }
}
